import type { FC } from "react";

import { MoneyWavyIcon, SubtractIcon, WalletIcon } from "@phosphor-icons/react";

import { Button, clsx, Icon } from "@alpacahq/ui/old";

import { Card } from "../../../../../components";
import { getPrettyCash } from "../../../../../utils/formatting";
import { WavyBackground } from "../WavyBackground";
import { useProgramsLayout } from "./LayoutCalculatorHook";
import { MetricColumn } from "./MetricColumn";
import { ProgramColumn } from "./ProgramColumn";

export interface ProgramsContainerPresProps {
  buyingPower?: number;
  cash?: number;
  dailyChange?: number;
  stockLending?: number;
  highYieldCash?: number;
  isBalancesLoading?: boolean;
  isStockLendingLoading?: boolean;
  isHighYieldCashLoading?: boolean;
  onBalancesClick?: () => void;
  onInterestPaidClick?: () => void;
}

export const ProgramsContainerPres: FC<ProgramsContainerPresProps> = ({
  buyingPower = 0,
  cash = 0,
  dailyChange = 0,
  stockLending = 0,
  highYieldCash = 0,
  isBalancesLoading = false,
  isStockLendingLoading = false,
  isHighYieldCashLoading = false,
  onBalancesClick,
  onInterestPaidClick,
}) => {
  const displays = {
    buyingPower: buyingPower
      ? getPrettyCash(buyingPower, {
          acceptZero: true,
          forcePrecision: 2,
          minimumFractionDigits: 2,
        })
      : "$0",
    cash: cash
      ? getPrettyCash(cash, {
          acceptZero: true,
          forcePrecision: 2,
          minimumFractionDigits: 2,
        })
      : "$0",
    dailyChange:
      dailyChange === 0
        ? "$0"
        : getPrettyCash(dailyChange, {
            forcePrecision: 2,
            minimumFractionDigits: 2,
          }),
    stockLending: stockLending
      ? getPrettyCash(stockLending, {
          acceptZero: true,
          forcePrecision: 2,
          minimumFractionDigits: 2,
        })
      : "~$0",
    highYieldCash: highYieldCash
      ? getPrettyCash(highYieldCash, {
          acceptZero: true,
          forcePrecision: 2,
          minimumFractionDigits: 2,
        })
      : "$0",
  };

  const config = {
    showFpsl: stockLending > 0,
    showHyc: highYieldCash > 0,
  };

  const showProgramsSection = config.showFpsl || config.showHyc;
  const dailyChangeColor =
    dailyChange >= 0 ? "text-emerald-500" : "text-red-500";

  const { containerRef, isVertical, hasBothPrograms } = useProgramsLayout(
    displays,
    config,
  );

  return (
    <Card className="relative overflow-hidden p-6">
      <WavyBackground />
      {/* Attach ref to measure available width */}
      <div ref={containerRef} className="w-full">
        <div
          className={clsx(
            "flex w-full justify-between gap-y-4 transition-all duration-200",
            isVertical ? "flex-col" : "flex-row items-stretch gap-x-5",
          )}
        >
          {/* LEFT SECTION: Balances */}
          <div
            className={clsx(
              "flex flex-col gap-y-4",
              isVertical
                ? "w-full"
                : showProgramsSection
                  ? hasBothPrograms
                    ? "basis-[58%]"
                    : "basis-[70%]"
                  : "flex-1",
            )}
          >
            <div className="flex items-center gap-x-2">
              <WalletIcon size={26} className="shrink-0" />
              <span className="whitespace-nowrap font-medium text-base text-gray-800">
                Balances
              </span>
              {onBalancesClick && (
                <Button
                  onClick={onBalancesClick}
                  className="ml-auto flex items-center justify-center rounded-full bg-yellow-300 px-1 py-1 hover:bg-yellow-500"
                  aria-label="View balances page"
                >
                  <Icon
                    name="ArrowRight"
                    className="h-3 w-3 stroke-3 text-black"
                  />
                </Button>
              )}
            </div>

            <div
              className={clsx(
                "flex w-full gap-y-4",
                isVertical
                  ? "flex-col items-start"
                  : "flex-row items-start justify-between gap-x-2",
              )}
            >
              <MetricColumn
                label="Buying Power"
                value={displays.buyingPower}
                isLoading={isBalancesLoading}
              />
              <MetricColumn
                label="Cash"
                value={displays.cash}
                isLoading={isBalancesLoading}
              />
              <MetricColumn
                label="Daily Change"
                value={displays.dailyChange}
                color={dailyChangeColor}
                isLoading={isBalancesLoading}
              />
            </div>
          </div>

          {/* DIVIDER */}
          {showProgramsSection && (
            <div
              className={clsx(
                "shrink-0",
                isVertical ? "w-full py-0" : "flex items-center",
              )}
            >
              <div
                className={clsx(
                  "bg-gray-200",
                  isVertical ? "h-px w-full" : "h-[72px] w-px",
                )}
              />
            </div>
          )}

          {/* RIGHT SECTION: Programs */}
          {showProgramsSection && (
            <div
              className={clsx(
                "flex flex-col gap-y-3",
                isVertical
                  ? "w-full"
                  : hasBothPrograms
                    ? "basis-[42%]"
                    : "basis-[30%]",
              )}
            >
              <div className="flex items-center gap-x-2">
                <span className="mt-0.5 whitespace-nowrap font-medium text-base text-gray-800">
                  Interest Paid
                </span>
                {onInterestPaidClick && config.showFpsl && (
                  <Button
                    onClick={onInterestPaidClick}
                    className="ml-auto flex items-center justify-center rounded-full bg-yellow-300 px-1 py-1 hover:bg-yellow-500"
                    aria-label="View stock lending interest history"
                  >
                    <Icon
                      name="ArrowRight"
                      className="h-3 w-3 stroke-3 text-black"
                    />
                  </Button>
                )}
              </div>

              <div
                className={clsx(
                  "flex w-full gap-y-4",
                  isVertical
                    ? "flex-col items-start"
                    : "flex-row items-start justify-between gap-x-2",
                )}
              >
                {config.showFpsl && (
                  <ProgramColumn
                    icon={<SubtractIcon size={26} className="shrink-0" />}
                    label="Stock Lending"
                    value={displays.stockLending}
                    isLoading={isStockLendingLoading}
                    prefix="~"
                  />
                )}
                {config.showHyc && (
                  <ProgramColumn
                    icon={<MoneyWavyIcon size={26} className="shrink-0" />}
                    label="High Yield Cash"
                    value={displays.highYieldCash}
                    isLoading={isHighYieldCashLoading}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default ProgramsContainerPres;
